import { getScopesLookup, latestEgress } from '@atlassian/mpac-api-client';
import { useStatsig } from '@atlassian/mpac-feature-flags';
import { BillingSystem } from '@atlassian/mpac-types';
import { hooks, log } from '@atlassian/mpac-utils';
import { Either, Option } from 'funfix-core';
import { appConfig } from '../../../../constants/Configuration';
import { ARI_TYPE } from '../../../../records/InstallableAppIdentifier';
import { getCloudIdFromHostInfo } from '../../../../services/HostInfoApi';
import getSPI from '../../../../services/SPI';
import { useEmceeAppInstallCache } from '../../contexts/emceeAppInstallCacheContext';
import { INSTALLABLE_APP_IDENTIFIER, MARKETPLACE_APP_KEY } from '../../types';
import { getAppEditionAndEligibleOfferings, getInstallableApp } from '../../utils/installableApp';
import { getAppInstallMode } from '../../utils/installMode';
const {
  usePromise
} = hooks;
export function getHostConfig(product) {
  const hostConfig = appConfig.get(product);
  if (!hostConfig) {
    throw new Error('Host product configuration is not defined for ' + product);
  }
  return hostConfig;
}
async function getHostData(hostInfo, hostPricing) {
  try {
    const hostLicense = await (hostPricing ? Promise.resolve(hostPricing) : getSPI().getHostPricingInfo());
    return {
      hostInfo,
      hostLicense,
      hostConfig: getHostConfig(hostInfo.product)
    };
  } catch (error) {
    throw new Error('Error building host data ' + error);
  }
}
const getAllScopes = async graphQLGatewayUrl => {
  return getScopesLookup(graphQLGatewayUrl).then(scopesLookup => Option.some(Either.right(scopesLookup)), error => Option.some(Either.left(error.status.toString())));
};

// eslint-disable-next-line max-lines-per-function
export function useAppInstallBaseData(mpacBaseUrl, graphQLGatewayUrl, environment, installIdentifier, hostInfo, installDialogStatsigClientInitialised) {
  const {
    enableSelfServeAppEditions
  } = useStatsig();
  const emceeAppInstallCache = useEmceeAppInstallCache();
  const getFromEmceeCache = async cache => {
    if (!cache.result) {
      throw new Error('Cached data from EMCEE is falsy');
    }
    if (!hostInfo) {
      throw new Error('hostInfo from useMarketplaceAppInstall is null/undefined');
    }
    const {
      hostLicense,
      scopesLookup
    } = cache.result;
    return Promise.all([Promise.resolve(getHostData(hostInfo, hostLicense)), Promise.resolve(scopesLookup), installIdentifier !== null && installIdentifier.type === INSTALLABLE_APP_IDENTIFIER && Promise.resolve(installIdentifier.keyForInstall)]);
  };
  const getFromNetwork = async () => {
    if (!hostInfo) {
      throw new Error('hostInfo from useMarketplaceAppInstall is null/undefined');
    }
    return Promise.all([getHostData(hostInfo), getAllScopes(graphQLGatewayUrl), installIdentifier !== null && installIdentifier.type === MARKETPLACE_APP_KEY && getInstallableApp(graphQLGatewayUrl, installIdentifier.keyForInstall)]);
  };
  const baseAppInstallPromise = async () => {
    const editionsAndOfferingsPromise = enableSelfServeAppEditions() && installIdentifier ? getAppEditionAndEligibleOfferings(graphQLGatewayUrl, installIdentifier.keyForInstall.key || installIdentifier.keyForInstall.appKey || installIdentifier.keyForInstall, getCloudIdFromHostInfo(hostInfo) || '', enableSelfServeAppEditions()) : undefined;
    const baseDataPromise = emceeAppInstallCache !== null ? getFromEmceeCache(emceeAppInstallCache) : getFromNetwork();
    const [[hostData, scopesLookup, appIdentifier], editionsResponse] = await Promise.all([baseDataPromise, editionsAndOfferingsPromise]);
    const shouldEnableReactivateForgeAppFromEmcee = hostData.hostLicense.billingSystem === BillingSystem.CCP;
    const installMode = await getAppInstallMode({
      mpacBaseUrl,
      hostInfo: hostData.hostInfo,
      appIdentifier,
      flags: {
        enableReactivateForgeAppFromEmcee: shouldEnableReactivateForgeAppFromEmcee
      },
      hostCmtEnabled: hostData.hostLicense.hostCmtEnabled
    });
    const envForForgeAppEgressAddresses = environment === 'prod' ? 'production' : 'staging';
    const forgeAppEgressAddresses = appIdentifier.type === ARI_TYPE ? await latestEgress(graphQLGatewayUrl, appIdentifier.ari, envForForgeAppEgressAddresses) : null;
    return {
      hostData,
      appIdentifier,
      scopesLookup,
      installMode,
      forgeAppEgressAddresses,
      editions: editionsResponse === null || editionsResponse === void 0 ? void 0 : editionsResponse.editions,
      eligibleOfferings: editionsResponse === null || editionsResponse === void 0 ? void 0 : editionsResponse.eligibleOfferings
    };
  };
  const runPromise = hostInfo !== null && installIdentifier !== null && (!emceeAppInstallCache || !emceeAppInstallCache.pending) && installDialogStatsigClientInitialised;
  const baseAppInstallPromiseDependencies = emceeAppInstallCache ? [installIdentifier, hostInfo, emceeAppInstallCache.pending, installDialogStatsigClientInitialised] : [installIdentifier, hostInfo, installDialogStatsigClientInitialised];
  const {
    pending,
    result,
    error
  } = usePromise(baseAppInstallPromise, baseAppInstallPromiseDependencies, runPromise);
  if (error) {
    log.error('AppInstallDialog', 'Failed to build appInstallBaseData', {
      error
    });
  }
  return {
    pending,
    error,
    result
  };
}